// FormField.tsx
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Check, Eye, EyeOff, Search } from "lucide-react";
import { Textarea } from "../ui/textarea";
import ErrorMessage from "./ErrorMessage";
import { Button } from "../ui/button";

type BaseProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  React.SelectHTMLAttributes<HTMLSelectElement>;

type SelectOption = string | { value: string; label: string; isDisabled?: boolean };

interface FormFieldProps extends Omit<BaseProps, "type"> {
  label?: string;
  error?: string;
  suffix?: string;
  placeholder?: string;
  type?:
  | "text"
  | "password"
  | "number"
  | "email"
  | "select"
  | "textarea"
  | "checkbox"
  | "search"
  | "radio"
  | "date"
  | "time"
  | "datetime"
  | "month"
  | "switch"
  | "file"
  options?: SelectOption[];
  rows?: number;
  defaultChecked?: boolean;
  isHidden?: boolean;
  className?: string;
  name?: string;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  placeholder,

  type = "text",
  suffix,
  options = [],
  rows = 3,
  name,
  isHidden = false,
  defaultChecked = false,
  className,
  leftIcon,
  checked,
  onCheckedChange,
  value,
  ...rest
}) => {
  const safeId = `${name || label || String(type)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

  // only used for password toggle
  const [showPassword, setShowPassword] = useState(false);

  const renderLabel = (id: string) =>
    type !== "checkbox" && type !== "radio" && type !== "datetime" && type !== "switch" && label ? (
      <label htmlFor={id} className="inline-block text-sm! font-medium mb-2">
        {label}
      </label>
    ) : null;

  // ✅ Switch type handler
  if (type === "switch") {
    return (
      <div className="flex items-center justify-between">
        {label && (
          <label htmlFor={safeId} className="text-sm! font-medium">
            {label}
          </label>
        )}
        <Switch
          id={safeId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className={className}
        />
      </div>
    );
  }
  if (type === "search") {
    return (
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        {renderLabel(safeId)}
        <Input
          id={safeId}
          type="search"
          placeholder={placeholder}
          name={name}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          className={`w-full bg-white px-3 text-sm! pl-10 ${className || ""}`}
        />
      </div>
    );
  }
  if (type === "file") {
    return (
      <div className="">
        {renderLabel(safeId)}
        <label htmlFor={safeId} className="w-24 h-24 rounded-full border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-muted-foreground transition-colors overflow-hidden">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload w-8 h-8 text-muted-foreground">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4">
            </path>
            <polyline points="17 8 12 3 7 8">
            </polyline>
            <line x1="12" x2="12" y1="3" y2="15">
            </line>
          </svg>
        </label>
        <Input
          id={safeId}
          type="file"
          name={name}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          className={`bg-white hidden px-3 text-sm! ${className || ""}`}
        />

        <ErrorMessage errors={error} />
      </div>
    );
  }
  if (type === "select") {
    return (
      <div className="">
        {renderLabel(safeId)}
        <Select
          disabled={rest.disabled}
          value={value as string}
          onValueChange={(val) =>
            rest.onChange?.({
              target: { name, value: val },
            } as any)
          }>
          <SelectTrigger disabled={rest.disabled} className={`w-full h-9 px-3 text-sm! bg-white ${className || ""}`}>
            {leftIcon && (
              <span className="mr-2 flex items-center">
                {leftIcon}
              </span>
            )}
            <SelectValue placeholder={placeholder || "Select an option"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option, index) =>
              typeof option === "string" ? (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ) : (
                <SelectItem key={index} value={option.value} disabled={option.isDisabled}>
                  {option.label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
        <ErrorMessage errors={error} />
      </div>
    );
  }

  if (type === "number") {
    return (
      <div className="relative">
        {/* suffix (if provided) */}
        {renderLabel(safeId)}
        <div className="relative">
          {suffix && <span className="text-gray-600 ml-2 absolute left-2 top-1/2 transform -translate-y-1/2">{suffix}</span>}
          <Input
            id={safeId}
            name={name}
            type="number"
            placeholder={placeholder}
            {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
            className={`w-full bg-white px-3 text-sm! ${className || ""}`}
            onWheel={(e) => e.currentTarget.blur()}
            min={0}
          />
        </div>
        <ErrorMessage errors={error} />
      </div>
    );
  }
  if (type === "textarea") {
    return (
      <div className="">
        {renderLabel(safeId)}
        <Textarea
          id={safeId}
          name={name}
          rows={rows}
          placeholder={placeholder}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          className={`w-full bg-white px-3 text-sm! ${className || ""}`}
        />
        <ErrorMessage errors={error} />
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          id={safeId}
          type="checkbox"
          name={name}
          defaultChecked={defaultChecked}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          className="w-4 h-4 peer hidden"
        />
        <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
          <Check color="#fff" />
        </span>
        <span>{label}</span>
      </label>
    );
  }

  if (type === "radio") {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer relative pl-8">
        <input
          id={safeId}
          type="radio"
          name={name}
          defaultChecked={defaultChecked}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          className="peer hidden"
        />
        <span className="w-6 h-6 rounded-full border-2 border-light-40 flex items-center justify-center peer-checked:border-primary absolute top-0 left-0"></span>
        <span className="w-3.5 h-3.5 rounded-full bg-primary hidden peer-checked:block absolute top-[5px] left-[5px]"></span>
        <span>{label}</span>
      </label>
    );
  }

  if (type === "datetime") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${safeId}-date`} className="inline-block text-sm! font-medium mb-2">
            {label ? `${label} Date` : "Date"}
          </label>
          <Input
            id={`${safeId}-date`}
            type="date"
            name={`${name}-date`}
            {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        </div>
        <div>
          <label htmlFor={`${safeId}-time`} className="inline-block text-sm! font-medium mb-2">
            {label ? `${label} Time` : "Time"}
          </label>
          <Input
            id={`${safeId}-time`}
            type="time"
            name={`${name}-time`}
            {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        </div>
      </div>
    );
  }

  // Default input & password handling
  const isPassword = type === "password";
  const inputType = isPassword ? (showPassword ? "text" : "password") : (type as string);

  return (
    <div className="relative">
      {renderLabel(safeId)}
      <div className="relative">
        <Input
          id={safeId}
          type={inputType}
          name={name}
          placeholder={placeholder}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          className={`bg-white px-3 pr-9 text-sm!   ${className || ""}`}
          aria-label={rest["aria-label"] || name || label || undefined}
        />

        {/* suffix (if provided) */}
        {suffix && <span className="text-gray-600 ml-2 absolute left-2 top-1/2 transform -translate-y-1/2">{suffix}</span>}

        {/* password toggle button */}
        {isPassword && (
          <Button
            type="button"
            size="icon"
            variant={"ghost"}
            onClick={() => setShowPassword((s) => !s)}
            className="absolute top-1/2 right-3! -translate-y-1/2! p-0!"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </Button>
        )}
      </div>
      <ErrorMessage errors={error} />
    </div>
  );
};

export default FormField;